/**
 * Az élő közvetítés beállításai. Mindkettő TARTÓS (`localStorage`).
 *
 * ⚠️ AZ `enabled` KORÁBBAN SZÁNDÉKOSAN NEM VOLT TARTÓS, és ezt a döntést
 * 2026-09-22-én megfordítottuk. Az eredeti indok az volt, hogy senki ne
 * kerülhessen adásba anélkül, hogy tudna róla: a kapcsoló minden rögzítés
 * végén és minden appindításnál alaphelyzetbe állt.
 *
 * Élesben viszont pont ez okozott hibát: a streamelő befejezett egy menetet,
 * indított egy újat, és a nézőoldal némán a régi záróképernyőn maradt, mert a
 * közvetítés közben magától kikapcsolt. Egy stream alatt több rögzítés is
 * lehet, és a kapcsolót nem életszerű mindegyiknél újra megkeresni.
 *
 * Amit ez a döntés MEGKÖVETEL: a felület mondja ki, hogy a közvetítés
 * bekapcsolva marad, amíg ki nem kapcsolják — lásd `LiveStreamScreen`.
 *
 * A mintát a `graphicsSettings.ts`-től veszi (olvasás, feliratkozás,
 * frissítés), hogy a beállítóképernyők egyformán működjenek.
 */

const DELAY_KEY = 'grundo.live.delayMs';
const ENABLED_KEY = 'grundo.live.enabled';

/** Live broadcasting is currently reserved for the app owner. */
export function canBroadcastLive(role: string | null | undefined): boolean {
  return role === 'owner';
}

/** Alapértelmezett csúszás: nagyjából együtt fut a Twitch saját késésével. */
export const DEFAULT_LIVE_DELAY_MS = 15_000;

/**
 * A választható csúszás-értékek. A 0 benne van, de tudatos vállalás: akkor a
 * publikus link valós időben mutatja, hol tart a futó.
 */
export const LIVE_DELAY_CHOICES_MS = [0, 15_000, 30_000, 60_000] as const;

export interface LiveStreamSettings {
  /** Megy-e a közvetítés. Bekapcsolva marad, amíg ki nem kapcsolják. */
  enabled: boolean;
  /** Szándékos csúszás ezredmásodpercben. */
  delayMs: number;
}

export function normalizeDelayMs(value: unknown): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return DEFAULT_LIVE_DELAY_MS;
  /**
   * A legközelebbi felkínált érték — így egy kézzel átírt tárolóbejegyzés sem
   * hozhat létre olyan csúszást, amit a felület nem tud megjeleníteni.
   *
   * ⚠️ HOLTVERSENYNÉL A NAGYOBB NYER (`<=`), mert a nagyobb csúszás a
   * biztonságosabb: egy sérült érték soha ne csökkentse a védelmet.
   */
  let best: number = LIVE_DELAY_CHOICES_MS[0];
  for (const choice of LIVE_DELAY_CHOICES_MS) {
    if (Math.abs(choice - number) <= Math.abs(best - number)) best = choice;
  }
  return best;
}

function readDelay(): number {
  try {
    const raw = localStorage.getItem(DELAY_KEY);
    return raw === null ? DEFAULT_LIVE_DELAY_MS : normalizeDelayMs(JSON.parse(raw));
  } catch {
    return DEFAULT_LIVE_DELAY_MS;
  }
}

function readEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'on';
  } catch {
    return false;
  }
}

let current: LiveStreamSettings | null = null;
const listeners = new Set<() => void>();

export function liveStreamSettings(): LiveStreamSettings {
  current ??= { enabled: readEnabled(), delayMs: readDelay() };
  return current;
}

export function subscribeToLiveStreamSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function updateLiveStreamSettings(patch: Partial<LiveStreamSettings>): LiveStreamSettings {
  const previous = liveStreamSettings();
  const next: LiveStreamSettings = {
    enabled: patch.enabled ?? previous.enabled,
    delayMs: patch.delayMs === undefined ? previous.delayMs : normalizeDelayMs(patch.delayMs),
  };
  current = next;
  try {
    if (next.delayMs !== previous.delayMs) {
      localStorage.setItem(DELAY_KEY, JSON.stringify(next.delayMs));
    }
    if (next.enabled !== previous.enabled) {
      if (next.enabled) localStorage.setItem(ENABLED_KEY, 'on');
      else localStorage.removeItem(ENABLED_KEY);
    }
  } catch {
    /* A beállítás az aktuális munkamenetben ettől még él. */
  }
  for (const listener of listeners) listener();
  return next;
}

/** Csak tesztekhez: a következő olvasás ismét a tárolóból induljon. */
export function resetLiveStreamSettingsCache(): void {
  current = null;
}
